/**
 * TakeAssessment — runner for admin-created interval assessments.
 *
 * Route: /take-assessment?id=<assessmentId>
 *
 * On completion:
 * - Emits a quiz TraitEvent through useTraitMutation (source: 'quiz')
 * - Appends to student.assessmentCompletions
 * - Navigates to /assessment-results
 */

import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { ChevronRight, ChevronLeft, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { StorageService } from "@/hooks/services/storageService";
import { AuthStore } from "@/hooks/stores/AuthStore";
import { useTraitMutation } from "@/hooks/useTraitMutation";
import { useActivityLog } from "@/hooks/useActivityLog";
import { AssessmentOption } from "@/components/ui/assessments/assessment-option";
import { AssessmentNavigation } from "@/components/ui/assessments/assessment-navigation";
import { AssessmentQuestion } from "@/components/ui/assessments/assessment-question";
import type { IntervalAssessment } from "@/models/assessments/intervalAssessment";

// ─────────────────────────────────────────────────────────────────────────────
// Progress bar
// ─────────────────────────────────────────────────────────────────────────────

const ProgressBar = ({ answered, total }: { answered: number; total: number }) => (
  <div className="w-full bg-white/20 rounded-full h-1.5">
    <div
      className="bg-white h-1.5 rounded-full transition-all duration-500"
      style={{ width: `${Math.round((answered / total) * 100)}%` }}
    />
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

// ── progress persistence ───────────────────────────────────────────────────

function getSaveKey(assessmentId: string) {
  return `es_progress_ta_${assessmentId}`;
}

function saveTaProgress(assessmentId: string, currentIndex: number, answers: Record<string, string>) {
  try {
    sessionStorage.setItem(getSaveKey(assessmentId), JSON.stringify({ currentIndex, answers }));
  } catch (_) {}
}

function loadTaProgress(assessmentId: string): { currentIndex: number; answers: Record<string, string> } | null {
  try {
    const raw = sessionStorage.getItem(getSaveKey(assessmentId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed.currentIndex !== "number") return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function clearTaProgress(assessmentId: string) {
  try { sessionStorage.removeItem(getSaveKey(assessmentId)); } catch (_) {}
}

// ─────────────────────────────────────────────────────────────────────────────

export default function TakeAssessment() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assessmentId = searchParams.get("id") ?? "";

  const { emitQuizResult } = useTraitMutation();
  const { logEvent } = useActivityLog();

  // Load assessment from StorageService
  const assessment = useMemo<IntervalAssessment | null>(() => {
    const all: IntervalAssessment[] = StorageService.read("intervalAssessments") ?? [];
    return all.find((a) => a.id === assessmentId) ?? null;
  }, [assessmentId]);

  // Load saved progress once at mount
  const savedProgress = useRef(assessmentId ? loadTaProgress(assessmentId) : null);

  const [currentIndex, setCurrentIndex] = useState(savedProgress.current?.currentIndex ?? 0);
  const [answers, setAnswers] = useState<Record<string, string>>(savedProgress.current?.answers ?? {});
  const [phase, setPhase] = useState<"question" | "computing" | "done">("question");
  const [isAnimating, setIsAnimating] = useState(false);

  // Auto-save whenever answers or position changes
  useEffect(() => {
    if (phase === "question" && assessmentId) {
      saveTaProgress(assessmentId, currentIndex, answers);
    }
  }, [currentIndex, answers, phase, assessmentId]);

  if (!assessment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 flex flex-col items-center justify-center px-6 text-center space-y-4">
        <div className="w-14 h-14 rounded-2xl bg-red-500/20 border border-red-400/30 flex items-center justify-center">
          <AlertCircle className="w-7 h-7 text-red-400" />
        </div>
        <h2 className="text-2xl font-bold text-white">Assessment not found</h2>
        <p className="text-slate-400 text-sm">This assessment may have been closed or removed.</p>
        <button
          onClick={() => navigate("/assessments")}
          className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl transition-all"
        >
          Back to Assessments
        </button>
      </div>
    );
  }

  const questions = assessment.questions;
  const currentQuestion = questions[currentIndex];
  const totalQuestions = questions.length;

  const handleSelect = (optionId: string) => {
    setAnswers((prev) => ({ ...prev, [currentQuestion.id]: optionId }));
  };

  const handleNext = () => {
    if (currentIndex < totalQuestions - 1) {
      setCurrentIndex((i) => i + 1);
    } else {
      finish();
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) setCurrentIndex((i) => i - 1);
  };

  const finish = () => {
    clearTaProgress(assessmentId);
    setPhase("computing");

    setTimeout(() => {
      try {
        // Aggregate trait deltas from all chosen options
        const traitDeltas: Record<string, number> = {};
        let totalWeight = 0;
        let maxWeight = 0;

        for (const question of questions) {
          const chosenOptionId = answers[question.id];
          const option = question.options.find((o) => o.id === chosenOptionId);
          if (!option) continue;
          for (const tw of option.traitWeights) {
            traitDeltas[tw.traitId] = (traitDeltas[tw.traitId] ?? 0) + tw.value;
            totalWeight += tw.value;
          }
          // Max possible weight per question
          const maxOptionWeight = Math.max(...question.options.map((o) =>
            o.traitWeights.reduce((s, tw) => s + tw.value, 0)
          ));
          maxWeight += maxOptionWeight;
        }

        const score = maxWeight > 0 ? Math.round((totalWeight / maxWeight) * 100) : 0;

        // Emit through mutation engine
        emitQuizResult({
          questionId: `${assessmentId}_complete`,
          courseName: assessment.title,
          rawTraitDeltas: traitDeltas,
          correct: true,
          timeTakenSeconds: undefined,
          timeLimitSeconds: undefined,
        });

        // Log platform event
        logEvent('quiz_attempted', {
          topicId: assessmentId,
          score,
          correct: Math.round(score / 10),
          total: questions.length,
        }, { outcomeScore: score });

        // Record completion on student record
        const currentUser = AuthStore.getState().currentUser;
        if (currentUser) {
          const students: any[] = StorageService.read("students") ?? [];
          const idx = students.findIndex((s) => s.userId === currentUser._id || s.email === currentUser.email);
          if (idx !== -1) {
            const completions = students[idx].assessmentCompletions ?? [];
            students[idx] = {
              ...students[idx],
              assessmentCompletions: [
                ...completions,
                {
                  assessmentId,
                  completedAt: new Date().toISOString(),
                  score,
                },
              ],
            };
            StorageService.write("students", students);
          }
        }
      } catch (err) {
        console.error("Failed to process assessment completion:", err);
      }
      setPhase("done");
    }, 1500);
  };

  // Computing screen
  if (phase === "computing") {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 px-6 text-center space-y-6">
        <Loader2 className="w-12 h-12 text-indigo-300 animate-spin" />
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-white">Processing your answers…</h2>
          <p className="text-slate-400">Updating your trait profile</p>
        </div>
      </div>
    );
  }

  // Done screen
  if (phase === "done") {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 px-6 text-center space-y-6">
        <div className="w-16 h-16 rounded-full bg-green-500/20 border border-green-400/30 flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-green-400" />
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-white">Assessment Complete!</h2>
          <p className="text-slate-400">Your trait profile has been updated based on your answers.</p>
        </div>
        <button
          onClick={() => navigate("/assessment-results")}
          className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl transition-all"
        >
          View My Profile <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  const wasResumed = savedProgress.current !== null && Object.keys(savedProgress.current.answers).length > 0;

  // Question screen
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Resume banner — shown once, dismissed on first interaction */}
      {wasResumed && currentIndex === (savedProgress.current?.currentIndex ?? 0) && phase === "question" && (
        <div className="bg-indigo-600 text-white text-sm text-center py-2 px-4 flex items-center justify-center gap-3">
          <span>Resuming from question {currentIndex + 1} — your previous answers are saved.</span>
          <button
            onClick={() => { savedProgress.current = null; setCurrentIndex(0); setAnswers({}); }}
            className="underline font-semibold hover:no-underline shrink-0"
          >
            Start over
          </button>
        </div>
      )}
      {/* Coloured header */}
      <div className="bg-gradient-to-r from-indigo-600 to-violet-700 px-6 pt-6 pb-4">
        <div className="max-w-2xl mx-auto space-y-2">
          <div className="flex items-center justify-between text-white/80 text-sm">
            <span className="font-semibold">{assessment.title}</span>
            <span>{currentIndex + 1} / {totalQuestions}</span>
          </div>
          <ProgressBar answered={currentIndex} total={totalQuestions} />
        </div>
      </div>

      {/* Question */}
      <div className="flex-1 flex flex-col justify-center px-6 py-8">
        <div className="max-w-2xl mx-auto w-full bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8 space-y-6">
          <AssessmentQuestion text={currentQuestion.text} isAnimating={isAnimating} />

          <div className="space-y-3">
            {currentQuestion.options.map((option) => (
              <AssessmentOption
                key={option.id}
                label={option.text}
                value={option.id}
                isSelected={answers[currentQuestion.id] === option.id}
                isAnimating={isAnimating}
                onClick={handleSelect}
              />
            ))}
          </div>

          <AssessmentNavigation
            onPrevious={handleBack}
            onNext={handleNext}
            canGoPrevious={currentIndex > 0}
            canGoNext={!!answers[currentQuestion.id]}
            isAnimating={isAnimating}
            isLastQuestion={currentIndex === totalQuestions - 1}
            currentQuestion={currentIndex + 1}
            totalQuestions={totalQuestions}
          />
        </div>
      </div>
    </div>
  );
}
